/**
 * Pattern Selector Component
 *
 * Displays extracted patterns, gaps, and insights from knowledge synthesis
 * and allows users to select which items to include in the generated document.
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useMemo } from 'react';
import { Card, Checkbox, Badge, Button, Tabs } from 'flowbite-react';
import { HiCheckCircle, HiExclamationCircle, HiLightBulb } from 'react-icons/hi';
import type { Pattern, KnowledgeGap, StrategyInsight } from './KnowledgeHarvestButton';

interface PatternSelectorProps {
  patterns: Pattern[];
  gaps: KnowledgeGap[];
  insights: StrategyInsight[];
  onSelectionChange: (selected: SelectedItems) => void;
  initialSelection?: SelectedItems;
}

export interface SelectedItems {
  patterns: Pattern[];
  gaps: KnowledgeGap[];
  insights: StrategyInsight[];
}

const PatternSelector: React.FC<PatternSelectorProps> = ({
  patterns,
  gaps,
  insights,
  onSelectionChange,
  initialSelection
}) => {
  const [selectedPatterns, setSelectedPatterns] = useState<Set<number>>(
    new Set(initialSelection?.patterns.map((_, i) => i) || [])
  );
  const [selectedGaps, setSelectedGaps] = useState<Set<number>>(
    new Set(initialSelection?.gaps.map((_, i) => i) || [])
  );
  const [selectedInsights, setSelectedInsights] = useState<Set<number>>(
    new Set(initialSelection?.insights.map((_, i) => i) || [])
  );

  // Update parent component whenever selection changes
  useMemo(() => {
    const selected: SelectedItems = {
      patterns: patterns.filter((_, i) => selectedPatterns.has(i)),
      gaps: gaps.filter((_, i) => selectedGaps.has(i)),
      insights: insights.filter((_, i) => selectedInsights.has(i))
    };
    onSelectionChange(selected);
  }, [selectedPatterns, selectedGaps, selectedInsights, patterns, gaps, insights, onSelectionChange]);

  const togglePattern = (index: number) => {
    const newSet = new Set(selectedPatterns);
    if (newSet.has(index)) {
      newSet.delete(index);
    } else {
      newSet.add(index);
    }
    setSelectedPatterns(newSet);
  };

  const toggleGap = (index: number) => {
    const newSet = new Set(selectedGaps);
    if (newSet.has(index)) {
      newSet.delete(index);
    } else {
      newSet.add(index);
    }
    setSelectedGaps(newSet);
  };

  const toggleInsight = (index: number) => {
    const newSet = new Set(selectedInsights);
    if (newSet.has(index)) {
      newSet.delete(index);
    } else {
      newSet.add(index);
    }
    setSelectedInsights(newSet);
  };

  const selectAll = () => {
    setSelectedPatterns(new Set(patterns.map((_, i) => i)));
    setSelectedGaps(new Set(gaps.map((_, i) => i)));
    setSelectedInsights(new Set(insights.map((_, i) => i)));
  };

  const selectNone = () => {
    setSelectedPatterns(new Set());
    setSelectedGaps(new Set());
    setSelectedInsights(new Set());
  };

  const totalSelected = selectedPatterns.size + selectedGaps.size + selectedInsights.size;

  return (
    <div className="space-y-4">
      {/* Selection controls */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {totalSelected} item{totalSelected !== 1 ? 's' : ''} selected
        </div>
        <div className="space-x-2">
          <Button size="xs" color="light" onClick={selectAll}>
            Select All
          </Button>
          <Button size="xs" color="light" onClick={selectNone}>
            Select None
          </Button>
        </div>
      </div>

      {/* Tabs for different categories */}
      <Tabs aria-label="Pattern categories">
        {/* Patterns tab */}
        <Tabs.Item
          active
          title={
            <span className="flex items-center">
              <HiCheckCircle className="w-4 h-4 mr-2" />
              Patterns ({patterns.length})
            </span>
          }
        >
          <div className="space-y-3">
            {patterns.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                No patterns discovered
              </div>
            ) : (
              patterns.map((pattern, index) => (
                <Card key={index} className="hover:bg-gray-50">
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      checked={selectedPatterns.has(index)}
                      onChange={() => togglePattern(index)}
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-gray-900">
                          {pattern.name || pattern.title || 'Unnamed Pattern'}
                        </h4>
                        <div className="flex items-center space-x-2">
                          {pattern.confidence && (
                            <Badge color="blue" size="sm">
                              {(pattern.confidence * 100).toFixed(0)}% confidence
                            </Badge>
                          )}
                          {pattern.sentiment && (
                            <Badge
                              color={
                                pattern.sentiment === 'positive'
                                  ? 'green'
                                  : pattern.sentiment === 'negative'
                                  ? 'red'
                                  : 'gray'
                              }
                              size="sm"
                            >
                              {pattern.sentiment}
                            </Badge>
                          )}
                        </div>
                      </div>
                      {pattern.description && (
                        <p className="text-sm text-gray-600 mt-2">
                          {pattern.description}
                        </p>
                      )}
                      {pattern.context && (
                        <p className="text-xs text-gray-500 mt-1">
                          Context: {pattern.context}
                        </p>
                      )}
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </Tabs.Item>

        {/* Knowledge Gaps tab */}
        <Tabs.Item
          title={
            <span className="flex items-center">
              <HiExclamationCircle className="w-4 h-4 mr-2" />
              Gaps ({gaps.length})
            </span>
          }
        >
          <div className="space-y-3">
            {gaps.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                No knowledge gaps identified
              </div>
            ) : (
              gaps.map((gap, index) => (
                <Card key={index} className="hover:bg-gray-50">
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      checked={selectedGaps.has(index)}
                      onChange={() => toggleGap(index)}
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-gray-900">
                          {gap.title || 'Knowledge Gap'}
                        </h4>
                        <div className="flex items-center space-x-2">
                          {gap.severity && (
                            <Badge
                              color={
                                gap.severity === 'high'
                                  ? 'red'
                                  : gap.severity === 'medium'
                                  ? 'yellow'
                                  : 'gray'
                              }
                              size="sm"
                            >
                              {gap.severity} severity
                            </Badge>
                          )}
                          {gap.priority && (
                            <Badge color="purple" size="sm">
                              {gap.priority} priority
                            </Badge>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mt-2">
                        {gap.description}
                      </p>
                      {gap.recommendation && (
                        <div className="mt-2 p-2 bg-blue-50 rounded">
                          <p className="text-xs text-blue-900">
                            <strong>Recommendation:</strong> {gap.recommendation}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </Tabs.Item>

        {/* Strategic Insights tab */}
        <Tabs.Item
          title={
            <span className="flex items-center">
              <HiLightBulb className="w-4 h-4 mr-2" />
              Insights ({insights.length})
            </span>
          }
        >
          <div className="space-y-3">
            {insights.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                No strategic insights generated
              </div>
            ) : (
              insights.map((insight, index) => (
                <Card key={index} className="hover:bg-gray-50">
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      checked={selectedInsights.has(index)}
                      onChange={() => toggleInsight(index)}
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-gray-900">
                          {insight.title}
                        </h4>
                        {insight.impact && (
                          <Badge
                            color={
                              insight.impact === 'high'
                                ? 'red'
                                : insight.impact === 'medium'
                                ? 'yellow'
                                : 'gray'
                            }
                            size="sm"
                          >
                            {insight.impact} impact
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-2">
                        {insight.description}
                      </p>
                      {insight.recommendation && (
                        <div className="mt-2 p-2 bg-green-50 rounded">
                          <p className="text-xs text-green-900">
                            <strong>Recommendation:</strong> {insight.recommendation}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </Tabs.Item>
      </Tabs>
    </div>
  );
};

export default PatternSelector;
